import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, CheckCircle, XCircle, Clock, TrendingUp,
  RefreshCw, FileCheck, AlertTriangle
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const API = (process.env.REACT_APP_API_URL || "https://api-vietnam.finkraftai.com") + "/api/gst";

function KPI({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className={`text-2xl font-bold ${color||"text-gray-900"}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color?.includes("green")?"bg-green-100":color?.includes("red")?"bg-red-100":color?.includes("blue")?"bg-blue-100":"bg-gray-100"}`}>
          <Icon className={`w-5 h-5 ${color||"text-gray-600"}`}/>
        </div>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/dashboard`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] gap-3 text-gray-400">
      <RefreshCw className="w-6 h-6 animate-spin text-[#367588]"/>
      <span>Loading dashboard</span>
    </div>
  );

  if (error) return (
    <div className="p-8">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3"/>
        <p className="text-red-800 font-semibold">Failed to load dashboard</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button onClick={load} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Retry</button>
      </div>
    </div>
  );

  const t = data?.totals || {};
  const sessions = data?.sessions || [];

  // Build chart data from sessions (last 10, reversed for chronological order)
  const chartData = [...sessions].reverse().slice(-10).map(s => ({
    name: (s.filename||"").slice(0,15),
    date: new Date(s.uploaded_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"}),
    total: s.stats?.total_rows || 0,
    clean: s.stats?.clean_rows || 0,
    errors: s.stats?.error_rows || 0,
    pdfs: s.stats?.pdf_generated || 0,
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1800px] mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Live data {data?.total_sessions || 0} upload sessions</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading?"animate-spin":""}`}/>Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <KPI label="Total Sessions"    value={(data?.total_sessions||0).toLocaleString()}           color="text-gray-900"   icon={FileText}/>
        <KPI label="Total Rows"        value={(t.total_rows||0).toLocaleString()}                    color="text-gray-900"   icon={TrendingUp}
             sub={`${(t.b2b_rows||0).toLocaleString()} B2B · ${(t.b2c_rows||0).toLocaleString()} B2C`}/>
        <KPI label="Clean Rows"        value={(t.clean_rows||0).toLocaleString()}                    color="text-green-700"  icon={CheckCircle}
             sub={t.total_rows > 0 ? `${Math.round((t.clean_rows||0)/(t.total_rows)*100)}% of total` : "—"}/>
        <KPI label="Has Errors"        value={(t.error_rows||0).toLocaleString()}                    color="text-red-600"    icon={XCircle}
             sub={t.total_rows > 0 ? `${Math.round((t.error_rows||0)/(t.total_rows)*100)}% of total` : "—"}/>
        <KPI label="PDFs Generated"    value={(t.pdf_generated||0).toLocaleString()}                 color="text-blue-700"   icon={FileCheck}
             sub={`${t.clean_rows > 0 ? Math.round((t.pdf_generated||0)/(t.clean_rows)*100) : 0}% of clean rows`}/>
        <KPI label="Total GST (₹)"     value={"₹"+(t.total_gst||0).toLocaleString("en-IN",{maximumFractionDigits:0})} color="text-[#367588]"/>
        <KPI label="B2B Transactions"  value={(t.b2b_rows||0).toLocaleString()}                      color="text-blue-700"/>
        <KPI label="B2C Transactions"  value={(t.b2c_rows||0).toLocaleString()}                      color="text-purple-700"/>
      </div>

      {/* Charts row */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Rows per upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Rows per Upload Session</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{top:0,right:10,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{fontSize:11}} tickLine={false}/>
                <YAxis tick={{fontSize:11}} tickLine={false} axisLine={false} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                <Tooltip formatter={(v,n)=>[v.toLocaleString("en-IN"), n]}/>
                <Bar dataKey="clean"  name="Clean"  fill="#22c55e" radius={[3,3,0,0]}/>
                <Bar dataKey="errors" name="Errors" fill="#ef4444" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Clean rate trend */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Total Rows Trend</h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{top:0,right:10,bottom:0,left:0}}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#367588" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#367588" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{fontSize:11}} tickLine={false}/>
                <YAxis tick={{fontSize:11}} tickLine={false} axisLine={false} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                <Tooltip formatter={v=>[v.toLocaleString("en-IN"),"Rows"]}/>
                <Area type="monotone" dataKey="total" stroke="#367588" strokeWidth={2} fill="url(#colorTotal)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sessions table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Upload Sessions <span className="text-gray-400 font-normal">({sessions.length})</span></h2>
          <button onClick={()=>navigate("/admin/validate")}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#367588] text-white rounded-lg text-xs font-semibold hover:bg-[#2a5a6b]">
            <FileText className="w-3.5 h-3.5"/>Upload New File
          </button>
        </div>
        {sessions.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30"/>
            <p className="text-sm">No uploads yet — go to Validate Data to upload your first GST file</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["File Name","Uploaded At","Total Rows","Clean","Errors","B2B","B2C","PDFs Generated","Session ID"].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sessions.map(sess => {
                  const st = sess.stats || {};
                  return (
                    <tr key={sess.session_id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={()=>navigate(`/admin/validate`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#367588] flex-shrink-0"/>
                          <span className="text-sm font-medium text-gray-900 max-w-[200px] truncate">{sess.filename}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3"/>
                          {new Date(sess.uploaded_at).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{(st.total_rows||0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-sm font-medium text-green-700">
                          <CheckCircle className="w-3.5 h-3.5"/>
                          {(st.clean_rows||0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1 text-sm font-medium ${(st.error_rows||0)>0?"text-red-600":"text-gray-400"}`}>
                          <XCircle className="w-3.5 h-3.5"/>
                          {(st.error_rows||0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-blue-700 font-medium">{(st.b2b_rows||0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-purple-700 font-medium">{(st.b2c_rows||0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(st.pdf_generated||0)>0?"bg-green-100 text-green-800":"bg-gray-100 text-gray-500"}`}>
                          {(st.pdf_generated||0).toLocaleString()} PDFs
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-400">{sess.session_id?.slice(0,12)}…</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}